// frontend/src/screens/ReceiptWidget.jsx
// Persistent widget showing the receipt-upload queue. Bulk picker + per-file
// status. Lives on the Transactions tab; queue survives navigation.

import { useRef, useState } from "react";
import { C } from "./theme";
import { useReceiptQueue } from "./ReceiptQueueContext";

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const statusColor = (status) => {
  if (status === "done")    return C.success;
  if (status === "failed")  return C.red;
  if (status === "queued")  return C.muted;
  return C.accent;
};

export default function ReceiptWidget({ onReceiptSaved }) {
  const fileRef = useRef(null);
  const { items, addFiles, removeItem, clearDone, counts, processing } = useReceiptQueue();
  const [collapsed, setCollapsed] = useState(false);
  const prevDoneCount = useRef(counts.done);

  // Notify parent (PendingInbox) to refresh when any new receipt finishes.
  if (counts.done > prevDoneCount.current) {
    prevDoneCount.current = counts.done;
    setTimeout(() => onReceiptSaved?.(), 100);
  } else if (counts.done < prevDoneCount.current) {
    prevDoneCount.current = counts.done;
  }

  const pick = () => fileRef.current?.click();
  const onPick = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = ""; // allow re-picking the same file
  };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 0, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: collapsed ? "none" : `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>📸 Scan Receipts</div>
          {counts.active > 0 && (
            <span style={{ fontSize: 11, color: C.accent, background: C.accent + "15", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>
              {counts.active} processing
            </span>
          )}
          {counts.done > 0 && (
            <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>{counts.done} saved</span>
          )}
          {counts.failed > 0 && (
            <span style={{ fontSize: 11, color: C.red, fontWeight: 600 }}>{counts.failed} failed</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPick} style={{ display: "none" }} />
          <button onClick={pick} className="pill-outline-btn" style={{ padding: "6px 12px", fontSize: 12 }}>
            + Add
          </button>
          <button onClick={() => setCollapsed(c => !c)}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: C.muted, fontSize: 16, padding: 4 }}
            aria-label={collapsed ? "Expand" : "Collapse"}>
            {collapsed ? "▾" : "▴"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div style={{ padding: 12 }}>
          {items.length === 0 && (
            <div style={{ textAlign: "center", color: C.muted, fontSize: 13, padding: "20px 12px", lineHeight: 1.6 }}>
              Tap <strong>+ Add</strong> to upload one or many receipts. Each one is processed in the background — text is extracted on your device, then saved as a pending transaction for review.
            </div>
          )}

          {items.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {items.map(item => (
                <div key={item.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, display: "flex", alignItems: "center", gap: 10 }}>
                  {item.previewUrl && (
                    <img src={item.previewUrl} alt=""
                      style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, flexShrink: 0, background: C.card }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.name}
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>{fmtBytes(item.size)}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 11, color: statusColor(item.status), fontWeight: 600 }}>
                        {item.status === "done" && "✓ "}
                        {item.status === "failed" && "✗ "}
                        {item.statusText}
                      </div>
                      {item.status === "ocr" && item.progress > 0 && (
                        <div style={{ fontSize: 10, color: C.muted }}>{item.progress}%</div>
                      )}
                    </div>
                    {(item.status === "uploading" || item.status === "ocr") && (
                      <div style={{ height: 3, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${item.progress}%`, background: C.accent, transition: "width 0.2s" }} />
                      </div>
                    )}
                    {item.error && (
                      <div style={{ fontSize: 10, color: C.red, marginTop: 2 }}>{item.error}</div>
                    )}
                    {item.parsed && item.status === "done" && (
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                        ₹{item.parsed.amount?.toLocaleString("en-IN")} · {item.parsed.category} · {item.parsed.vendor}
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeItem(item.id)}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: C.muted, fontSize: 18, padding: 4, flexShrink: 0 }}
                    title="Remove from list (does not delete the saved transaction)">×</button>
                </div>
              ))}
              {(counts.done > 0 || counts.failed > 0) && (
                <button onClick={clearDone}
                  style={{ background: "transparent", border: "none", color: C.muted, fontSize: 11, padding: "6px 0", cursor: "pointer", textAlign: "left" }}>
                  Clear finished items
                </button>
              )}
            </div>
          )}

          {processing && items.some(i => i.status === "ocr") && (
            <div style={{ fontSize: 10, color: C.muted, marginTop: 8, padding: "6px 8px", background: C.surface, borderRadius: 6, lineHeight: 1.5 }}>
              First receipt downloads the OCR engine (~12MB). Subsequent receipts use the cached engine and process in 3-8 seconds each.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
